import { Recording } from '@app/routes/content/domain';
import {
  buildTitleFromDate,
  getDefaultThumbnailUrl,
} from '@app/routes/content/utils';
import { useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRecordingAnalytics } from '../../../hooks';
import { selectRecording } from '../../../selector';
import { SectionCard } from './section-card';

export const RelatedVideos = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const recording = useAppSelector(selectRecording) as Recording;
  const { related, parent_recording_id } = recording || {};
  const defaultThumbnailUrl = getDefaultThumbnailUrl();
  const { trackRecordingEvent } = useRecordingAnalytics();

  const handleClick = useCallback(
    (recordingId: string) => {
      trackRecordingEvent('ch_recording_editor_related_video_clicked');
      const url = `../../${recordingId}/editor`;
      const currentFullUrl = window.location.origin + location.pathname;
      const finalUrl = `${url}?backUrl=${encodeURIComponent(currentFullUrl)}`;
      navigate(finalUrl);
    },
    [navigate, trackRecordingEvent, location.pathname],
  );

  if (!related || related.length === 0) {
    return null;
  }

  return (
    <SectionCard className="bg-spring-neutral-base p-3">
      <div className="flex flex-col gap-3">
        <p className="text-white text-sm font-medium m-0">
          {t('content-recording.content-menu.related-videos')}
        </p>
        <div className="flex flex-col gap-2 justify-start items-center">
          {related.map((relatedRecording: Recording) => {
            const title =
              relatedRecording.title ||
              buildTitleFromDate({
                locale: 'en',
                title: relatedRecording?.title,
                startedAt: relatedRecording?.started_at || '',
              });
            if (relatedRecording.id === parent_recording_id) {
              return null;
            }
            return (
              <button
                key={relatedRecording.id}
                className="flex items-center gap-2 w-full"
                onClick={() => {
                  handleClick(relatedRecording.id);
                }}
                title={title}
                data-testid="related-video-navigation-button"
              >
                <img
                  className="h-6 w-auto rounded-[.25rem] object-contain"
                  src={relatedRecording.thumbnail || defaultThumbnailUrl}
                  alt={title}
                />
                <p className="text-white text-xs font-medium m-0 flex-1 min-w-0 line-clamp-1 text-left">
                  {title}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </SectionCard>
  );
};
